import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Wrench,
  FlaskConical,
  Play,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { callAdminBackfillVetaxisBootstrap } from '../../services/admin';

type BackfillResult = {
  ok: boolean;
  dryRun: boolean;
  scanned: number;
  processedCount: number;
  skippedCount: number;
  errorCount: number;
  processed: Array<{
    uid: string;
    email?: string | null;
    plan?: string;
    status?: string;
    action?: 'processed' | 'would_process';
  }>;
  skipped: Array<{
    uid: string;
    email?: string | null;
    plan?: string;
    status?: string;
    reason: string;
  }>;
  errors: Array<{
    uid: string;
    error: string;
  }>;
};

const statusPill = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v === 'active' || v === 'trialing') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
  if (v === 'past_due' || v === 'canceled') {
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const AdminVetAxisBackfill: React.FC = () => {
  const [targetUid, setTargetUid] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState<'preview' | 'run' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<BackfillResult | null>(null);

  const run = async (dryRun: boolean) => {
    setLoading(dryRun ? 'preview' : 'run');
    setError(null);
    setMessage(null);
    try {
      const response = await callAdminBackfillVetaxisBootstrap({
        targetUid: targetUid.trim() || undefined,
        limit,
        dryRun,
      });
      setResult(response);
      setMessage(
        dryRun
          ? `Preview complete. ${response.processedCount} user(s) are ready for VetAxis backfill.`
          : `Backfill complete. Processed ${response.processedCount} user(s).`
      );
    } catch (err: any) {
      setError(err?.message || 'VetAxis backfill failed');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin/vetaxis" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to VetAxis Ops
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Wrench className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">VetAxis Backfill</h1>
              <p className="text-slate-600 dark:text-gray-400">Repair missing VetAxis users, clinics, and memberships from suite entitlements.</p>
            </div>
          </div>
        </motion.div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 rounded-xl glass-effect border border-green-200 dark:border-green-800/30 text-sm text-green-700 dark:text-green-300 flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>{message}</span>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-sm text-red-700 dark:text-red-300 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </motion.div>
        )}

        <motion.div
          className="glass-effect rounded-2xl p-5 shadow-glass mb-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-sm text-slate-700 dark:text-gray-300">
              <span className="block mb-1">Target UID (optional)</span>
              <input
                value={targetUid}
                onChange={(e) => setTargetUid(e.target.value)}
                placeholder="Leave empty to scan multiple users"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <label className="text-sm text-slate-700 dark:text-gray-300">
              <span className="block mb-1">Scan limit (bulk mode)</span>
              <input
                type="number"
                min={1}
                max={500}
                value={limit}
                onChange={(e) => setLimit(Math.min(500, Math.max(1, Number(e.target.value || 1))))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <div className="flex items-end gap-2">
              <button
                onClick={() => run(true)}
                disabled={loading !== null}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <FlaskConical className="w-4 h-4" />
                {loading === 'preview' ? 'Previewing...' : 'Preview'}
              </button>
              <button
                onClick={() => run(false)}
                disabled={loading !== null}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                {loading === 'run' ? 'Running...' : 'Run Backfill'}
              </button>
            </div>
          </div>
        </motion.div>

        {result && (
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="glass-effect rounded-xl p-3">
                <p className="text-xs text-slate-500 dark:text-gray-400">Mode</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{result.dryRun ? 'Preview' : 'Write'}</p>
              </div>
              <div className="glass-effect rounded-xl p-3">
                <p className="text-xs text-slate-500 dark:text-gray-400">Scanned</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{result.scanned}</p>
              </div>
              <div className="glass-effect rounded-xl p-3">
                <p className="text-xs text-slate-500 dark:text-gray-400">Processed</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{result.processedCount}</p>
              </div>
              <div className="glass-effect rounded-xl p-3">
                <p className="text-xs text-slate-500 dark:text-gray-400">Skipped</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{result.skippedCount}</p>
              </div>
              <div className="glass-effect rounded-xl p-3">
                <p className="text-xs text-slate-500 dark:text-gray-400">Errors</p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{result.errorCount}</p>
              </div>
            </div>

            <div className="glass-effect rounded-2xl p-4 shadow-glass">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 inline-flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                Processed
              </h2>
              {result.processed.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-gray-400">No users processed in this run.</p>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-gray-700">
                        <th className="py-2 pr-3">UID</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Plan</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.processed.map((row) => (
                        <tr key={`processed-${row.uid}`} className="border-b border-slate-100 dark:border-gray-800">
                          <td className="py-2 pr-3 font-mono text-xs">{row.uid}</td>
                          <td className="py-2 pr-3">{row.email || '—'}</td>
                          <td className="py-2 pr-3 capitalize">{row.plan || '—'}</td>
                          <td className="py-2 pr-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(row.status || '')}`}>{row.status || '—'}</span>
                          </td>
                          <td className="py-2 pr-3">{row.action || 'processed'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="glass-effect rounded-2xl p-4 shadow-glass">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">Skipped</h2>
              {result.skipped.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-gray-400">No skipped users.</p>
              ) : (
                <div className="space-y-2">
                  {result.skipped.map((row) => (
                    <div key={`skipped-${row.uid}-${row.reason}`} className="rounded-lg border border-slate-200 dark:border-gray-700 px-3 py-2 text-sm">
                      <span className="font-mono text-xs">{row.uid}</span>
                      <span className="mx-2 text-slate-400">•</span>
                      <span>{row.email || 'No email'}</span>
                      <span className="mx-2 text-slate-400">•</span>
                      <span className="text-slate-600 dark:text-gray-300">{row.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="glass-effect rounded-2xl p-4 shadow-glass">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">Errors</h2>
              {result.errors.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-gray-400">No errors reported.</p>
              ) : (
                <div className="space-y-2">
                  {result.errors.map((row) => (
                    <div key={`error-${row.uid}`} className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/70 dark:bg-red-900/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                      <span className="font-mono text-xs">{row.uid}</span>
                      <span className="mx-2 text-red-300">•</span>
                      <span>{row.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminVetAxisBackfill;
